import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { makeStyles } from '@mui/styles'
import { Grid, Typography, Theme, Tooltip } from '@mui/material'
import { toast } from 'react-toastify'
import { useHistory, useLocation } from 'react-router-dom'

import { endOfDay, startOfDay } from 'date-fns'
import HelpIcon from '@mui/icons-material/HelpOutline'
import { useDispatch, useSelector } from 'react-redux'
import { DataPicker } from '../../components/DatePicker'
import { FilterSelect } from '../../components/FilterSelect'
import { Search } from '../../components/Search'
import { GreenButton } from '../../components/Buttons/GreenButton'
import { routes } from '../../constants/routes'
import { icons } from '../../constants/icons'
import { columns } from './usersList'
import { TableComponent } from '../../components/Table'
import { AppStateContext, useRest, useRefresh } from '../../components/Context'
import { CRM_KYC_STATUS } from '../../constants/crmKycStatus'
import useDebounce from '../../hooks/useDebounce'
import { UploadCsvFile } from '../UploadCsvFile'
import { generateParams } from '../../utils'
import { getUsersForSearchInput } from '../../store/selectors'
import { fetchUsersForReassignRequest } from '../../store/actions'
import { tabList } from '../UserProfile/UserProfileTabs/tabList'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: '100%',
    paddingTop: theme.spacing(4),
    '& p': {
      display: 'none',
    },
  },
  topWrap: {
    width: '100%',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  rightSide: {
    flexWrap: 'nowrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: 'max-content',
    paddingTop: theme.spacing(2),
    '& :first-child': {
      marginLeft: 0,
    },
  },
  title: {
    paddingTop: theme.spacing(2),
  },
  button: {
    paddingLeft: 15,
  },
  table: {
    paddingTop: theme.spacing(2),
  },
  tooltipWrap: {
    position: 'relative',
  },
  helpButton: {
    fontSize: 18,
    color: 'rgba(255,255,255,0.6)',
    '& svg': {
      fontSize: 18,
    },
  },
  tooltip: {
    position: 'absolute',
    top: -7,
    right: 7,
  },
}))

export const Users: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const { rxios } = useRest()
  const { saveIsRefresh } = useRefresh()
  const { getIsRefresh } = useContext(AppStateContext)
  const formMethods = useForm({
    mode: 'onBlur',
  })

  const dispatch = useDispatch()
  const introducerSelect = useSelector(getUsersForSearchInput)

  const { watch, getValues } = formMethods
  // eslint-disable-next-line
  const [startDateSelected, setStartDateSelected] = useState<any>('')
  // eslint-disable-next-line
  const [endDateSelected, setEndDateSelected] = useState<any>('')
  // eslint-disable-next-line
  const [statusSelected, setStatusSelected] = useState<any>('')
  // eslint-disable-next-line
  const [companySelected, setCompanySelected] = useState<any>('')
  // eslint-disable-next-line
  const [introducerSelected, setIntroducerSelected] = useState<any>('')
  // eslint-disable-next-line
  const [loading, setLoading] = useState<boolean>(false)
  const [isInitDate, setInitDate] = useState<boolean>(false)
  // eslint-disable-next-line
  const [values, setValues] = useState<any>({})
  // eslint-disable-next-line
  const [companySelect, setCompanySelect] = useState<any>([])
  const [queryString, setQueryString] = useState<string>('')
  const [searchDebounce, setSearchDebounce] = useState<string>('')
  const debouncedValue = useDebounce<string>(queryString, 200)
  const [refresh, setRefresh] = useState<boolean | undefined>()

  useEffect(() => setRefresh(getIsRefresh()), [setRefresh, getIsRefresh])

  const { search } = useLocation()
  const stringParams = new URLSearchParams(search)

  const pageUrl: number | string | null = stringParams.get('page')

  // eslint-disable-next-line
  useEffect(() => setSearchDebounce(queryString), [debouncedValue])

  const getUserId = useCallback(
    (userId) => {
      history.push(`${routes.USERS}/${userId}/${tabList[0].slug}`)
    },
    [history],
  )

  const pushParamsToURL = useCallback(
    (startDate, endDate, kyc, company, introducer, searchString: string) => {
      const currentUrlParams = new URLSearchParams(window.location.search)

      if (startDate) {
        currentUrlParams.set('startDate', startDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('startDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (endDate) {
        currentUrlParams.set('endDate', endDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('endDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (kyc) {
        currentUrlParams.set('kyc', kyc)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('kyc')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }

      if (company) {
        currentUrlParams.set('company', company)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('company')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }

      if (introducer) {
        currentUrlParams.set('introducer', introducer)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('introducer')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }

      if (searchString) {
        currentUrlParams.set('search', searchString)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('search')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }
    },
    [history],
  )

  const loadData = useCallback(
    async (page, kyc = '', company = '', introducer = '', startDate = '', endDate = '', searchString = '') => {
      let cleanupFunction = false
      setLoading(true)

      const searchSplit: string[] = searchString.split(' ')
      let userRolesSearch = searchString.trim().toUpperCase()

      if (searchSplit.length === 2) {
        userRolesSearch = userRolesSearch.replace(/\s/, '_')
      }

      rxios
        .get(
          `users?${generateParams({
            page,
            count: '10',
            company: company ? encodeURIComponent(company) : undefined,
            kyc: kyc ? kyc.toUpperCase().replace('_', ' ') : undefined,
            introducer,
            startDate,
            endDate,
            search: userRolesSearch,
          })}`,
        )
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setValues(next)
            history.push(`users?page=${page}`)

            pushParamsToURL(startDate, endDate, kyc, company, introducer, searchString)

            if (!cleanupFunction) setLoading(false)
          },
          (error) => {
            pushParamsToURL(startDate, endDate, kyc, company, introducer, searchString)
            toast.error(error)
            setLoading(false)
          },
          () => {
            setLoading(false)
          },
        )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [history, rxios],
  )

  const initData = useCallback(
    async (page) => {
      let cleanupFunction = false
      setLoading(true)
      rxios.get(`users?page=${page}&count=10`).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setValues(next)
          if (!cleanupFunction) setLoading(false)
          setInitDate(true)
          saveIsRefresh(false)
        },
        (error) => {
          toast.error(error)
          setLoading(false)
        },
        () => {
          setLoading(false)
        },
      )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [rxios],
  )

  const loadCompanyList = useCallback(async () => {
    setLoading(true)

    rxios.get(`company-names`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        const company = []

        // eslint-disable-next-line no-restricted-syntax
        for (const name of next) {
          company.push({
            value: name,
            label: name,
          })
        }
        setCompanySelect(company)
      },
      (error) => {
        toast.error(error)
      },
    )
    // eslint-disable-next-line
  }, [rxios])

  useEffect(() => {
    dispatch(fetchUsersForReassignRequest())
    ;(async () => {
      await initData(pageUrl || '1')
      await loadCompanyList()
    })()
    // eslint-disable-next-line
  }, [dispatch])

  useEffect(() => {
    if (refresh) {
      dispatch(fetchUsersForReassignRequest())
      ;(async () => {
        await initData(pageUrl || '1')
        await loadCompanyList()
      })()
    }
    // eslint-disable-next-line
  }, [refresh, dispatch])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'startDate') setStartDateSelected(value?.startDate?.toISOString())
      if (name === 'endDate') setEndDateSelected(value?.endDate?.toISOString())
      if (name === 'status') setStatusSelected(value?.status.toLowerCase().replace(' ', '_'))
      if (name === 'company') setCompanySelected(value?.company)
      if (name === 'introducer') setIntroducerSelected(value?.introducer)
      if (name === 'search') setQueryString(value?.[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    if (isInitDate) {
      setEndDateSelected(endDateSelected === '1970-01-01' ? '' : endDateSelected)
      ;(async () => {
        await loadData(
          1,
          statusSelected || '',
          companySelected || '',
          introducerSelected || '',
          startDateSelected || '',
          endDateSelected || '',
          searchDebounce || '',
        )
      })()
    }
    // eslint-disable-next-line
  }, [statusSelected, companySelected, introducerSelected, startDateSelected, endDateSelected, searchDebounce])

  return (
    <Grid className={classes.container}>
      <Grid container className={classes.topWrap}>
        <Grid item className={classes.title}>
          <Typography variant="h1">Users</Typography>
        </Grid>
        <FormProvider {...formMethods}>
          <Grid container item className={classes.rightSide}>
            <DataPicker
              placeholder={'Start Date'}
              margin={'0 8px'}
              dataPickerName={'startDate'}
              required={false}
              maxDate={getValues().endDate}
              formatValue={startOfDay}
            />
            <DataPicker
              placeholder={'End Date'}
              margin={'0 8px'}
              dataPickerName={'endDate'}
              required={false}
              minDate={getValues().startDate}
              formatValue={endOfDay}
            />

            <FilterSelect
              inputName={'status'}
              placeholder="KYC Status"
              enums={CRM_KYC_STATUS}
              mr={8}
              ml={8}
              width={138}
            />
            <FilterSelect inputName={'company'} placeholder="Company" enums={companySelect} mr={8} ml={8} width={138} />
            <FilterSelect
              inputName={'introducer'}
              placeholder="Introducer"
              enums={introducerSelect}
              mr={8}
              ml={8}
              width={138}
            />
            <Search inputName={'search'} />
            <Grid className={classes.tooltipWrap}>
              <UploadCsvFile type={'user'} />
              <Tooltip
                className={classes.tooltip}
                title={
                  <>
                    <Typography>CSV file structure for users uploading:</Typography>
                    <p>
                      First Name, Last Name, Email, Date of Birth, Country, City, Phone, User Type, Currency, Company
                      (optional)
                    </p>
                  </>
                }
              >
                <HelpIcon className={classes.helpButton} />
              </Tooltip>
            </Grid>
            <GreenButton width={120} prefix={icons.ADD} ml={8} text={'Add User'} href={routes.USER_CREATE} />
          </Grid>
        </FormProvider>
      </Grid>

      <Grid container className={classes.table}>
        <TableComponent
          getElementId={getUserId}
          columns={columns}
          data={values.items}
          params={values.meta}
          // eslint-disable-next-line
          loadData={(pageNumber: any) =>
            loadData(
              pageNumber,
              statusSelected || '',
              companySelected || '',
              introducerSelected || '',
              startDateSelected || '',
              endDateSelected || '',
              searchDebounce || '',
            )
          }
          loading={loading}
          twoAction
        />
      </Grid>
    </Grid>
  )
}
